import { useEffect, useRef, useState, type ReactNode } from "react";

export type IconName =
  | "sprout"
  | "arrow"
  | "play"
  | "pause"
  | "replay"
  | "page"
  | "people"
  | "grid"
  | "clock"
  | "check"
  | "branch"
  | "code"
  | "mail"
  | "chat"
  | "plus"
  | "asterisk"
  | "curvedArrow";
const paths: Record<IconName, ReactNode> = {
  sprout: (
    <>
      <path d="M12 21v-9M12 15C4 15 3 10 3 5c7 0 9 4 9 10ZM12 11C12 5 16 2 21 2c0 6-3 10-9 9Z" />
    </>
  ),
  arrow: <path d="M4 12h15m-6-6 6 6-6 6" />,
  play: <path d="m9 5 11 7-11 7Z" />,
  pause: <path d="M8 5v14M16 5v14" />,
  replay: (
    <>
      <path d="M4 10a8 8 0 1 1 1 8M4 4v6h6" />
    </>
  ),
  page: (
    <>
      <path d="M14 3H5v18h14V8Zm0 0v5h5M8 12h8M8 16h6" />
    </>
  ),
  people: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 21v-3a6 6 0 0 1 12 0v3M17 5a3 3 0 0 1 0 6m1 3a5 5 0 0 1 3 5v2" />
    </>
  ),
  grid: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 6v6l4 2" />
    </>
  ),
  check: <path d="m5 12 4 4L19 6" />,
  branch: (
    <>
      <circle cx="6" cy="5" r="2" />
      <circle cx="6" cy="19" r="2" />
      <circle cx="18" cy="5" r="2" />
      <path d="M6 7v10M18 7v2a5 5 0 0 1-5 5H6" />
    </>
  ),
  code: <path d="m8 6-6 6 6 6m8-12 6 6-6 6m-3-15-2 18" />,
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 6 9 7 9-7" />
    </>
  ),
  chat: <path d="M21 11a9 9 0 0 1-9 9H4l-2 2V11a9 9 0 0 1 19 0Z" />,
  plus: <path d="M12 5v14M5 12h14" />,
  asterisk: (
    <path d="M12 2v20M2 12h20M5 5l14 14M5 19 19 5M8 3l8 18M3 8l18 8M3 16l18-8M8 21l8-18" />
  ),
  curvedArrow: <path d="M3 3c12 0 15 5 15 17m-6-5 6 5 4-6" />,
};
export function Icon({
  name,
  className = "",
}: {
  name: IconName;
  className?: string;
}) {
  return (
    <svg
      className={`g-icon ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}
export function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return reduced;
}
export function useInView<T extends HTMLElement>(margin = "0px") {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);
  const [visited, setVisited] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting);
        if (entry.isIntersecting) setVisited(true);
      },
      { rootMargin: margin, threshold: 0.1 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [margin]);
  return { ref, visible, visited };
}
export function ExampleLabel() {
  return (
    <span className="g-example-label">
      <span /> Interactive example
    </span>
  );
}
